import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Open Razorpay checkout for the given plan id.
 * Returns a promise that resolves with { success, access } on payment + verification.
 */
export function useRazorpayCheckout() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const start = async (planId, { test = false } = {}) => {
    setSubmitting(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Couldn't load Razorpay. Check your internet and try again.");
        return;
      }
      const { data } = await api.post(
        "/billing/create-order",
        { plan_id: planId },
        { params: test ? { test: 1 } : {} },
      );
      if (!data.key_id) {
        toast.error("Payments aren't configured yet. Please try again shortly.");
        return;
      }

      return new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Brimi AI",
          description: `${data.plan.name} subscription`,
          order_id: data.order_id,
          prefill: {
            name: user?.name || data.user?.name || "",
            email: user?.email || data.user?.email || "",
          },
          theme: { color: "#1299E0" },
          modal: {
            ondismiss: () => {
              setSubmitting(false);
              resolve({ success: false, dismissed: true });
            },
          },
          handler: async (response) => {
            try {
              const verifyRes = await api.post("/billing/verify", {
                plan_id: planId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success("Payment successful! You're all set 🎉");
              if (refreshUser) await refreshUser();
              navigate("/subjects");
              resolve({ success: true, access: verifyRes.data.access });
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Payment verification failed");
              resolve({ success: false });
            } finally {
              setSubmitting(false);
            }
          },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed");
          setSubmitting(false);
          resolve({ success: false, error: resp.error });
        });
        rzp.open();
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
      return { success: false };
    }
  };

  return { start, submitting };
}
